import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Log } from 'oidc-client-ts';
import { AuthProvider } from 'react-oidc-context';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';

import { PermissionsList } from './constants/permissionsList';
import { DrawerProvider } from './context/DrawerContext/DrawerContext';
import { ModalProvider } from './context/ModalContext';
import { NavigationBarProvider } from './context/NavigationBarContext';
import { ToastProvider } from './context/ToastContext';
import useIsSignedIn from './hooks/useIsSignedIn';
import { QueryProvider } from './queryProvider';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Chatbot } from '@/components/ChatBot/chatBot';
import { useTokenAutoRefresh } from '@/hooks/useTokenAutoRefresh';
import { useTokenAutoRefreshOIDC } from '@/hooks/useTokenAutoRefreshOIDC';
import { refreshTokenConfig, oidcConfig } from '@/oidcConfig';
import AppRoutes from '@/routes';
import { RootState, store } from '@/store';

function WrappedApp() {
  Log.setLogger(console);
  Log.setLevel(Log.DEBUG);

  useTokenAutoRefreshOIDC();
  useTokenAutoRefresh();

  const auth = useSelector((state: RootState) => state.auth);
  const userIsSignedIn = useIsSignedIn();
  const refreshConfig = refreshTokenConfig(auth);
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const chatbotAllowed = userIsSignedIn && auditableElements?.['CHAT_BOT'];

  if (userIsSignedIn && refreshConfig.needRefresh) {
    return <ContainerLoader />;
  }

  return (
    <>
      <AppRoutes />
      {chatbotAllowed && <Chatbot />}
    </>
  );
}

function App() {
  return (
    <AuthProvider {...oidcConfig}>
      <Provider store={store}>
        <BrowserRouter>
          <DrawerProvider>
            <QueryProvider>
              <ModalProvider>
                <ToastProvider>
                  <NavigationBarProvider>
                    <WrappedApp />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </NavigationBarProvider>
                </ToastProvider>
              </ModalProvider>
            </QueryProvider>
          </DrawerProvider>
        </BrowserRouter>
      </Provider>
    </AuthProvider>
  );
}

export default App;
